import React from "react";
import { GroupBase, SelectInstance } from "react-select";
import {
  UseAsyncPaginateParams,
  withAsyncPaginate,
  ComponentProps,
} from "react-select-async-paginate";
import {
  SelectInputCreatable,
  SelectInputCreatableProps,
} from "./select-input-creatable";
import {
  OptionType,
  Control,
  DropdownIndicator,
  ClearIndicator,
  MultiValue,
  MultiValueRemove,
  Option,
  Menu,
  useStyles,
  useClassNames,
  defaultPlaceholder,
} from "./select-input";
import { ApiError } from "@/client/extend";
import {
  QueryKey,
  UseMutationResult,
  UseQueryOptions,
} from "@tanstack/react-query";
import {
  useOptionsMutationLoader,
  useOptionsQueryLoader,
} from "./options-cache";
import { CopyPasteProps, useCopyPaste } from "./copy-paste";
import { useMenuPortal } from "./menu-portal-context";
import { combineRefs } from "@/hooks/combine-refs";
import { firstNotUndefined } from "@/lib/utils";
import { SelectInputAsyncPaginateQuery } from "./select-input-async-pagination";

type SelectInputAsyncPaginateCreatableProps<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  Additional,
> = SelectInputCreatableProps<Value, Option, IsMulti, Group> &
  UseAsyncPaginateParams<Option, Group, Additional> &
  ComponentProps<Option, Group, IsMulti>;

interface SelectInputAsyncPaginateCreatablePropsType {
  <
    Value,
    Option extends OptionType<Value>,
    IsMulti extends boolean,
    Group extends GroupBase<Option>,
    Additional,
  >(
    props: SelectInputAsyncPaginateCreatableProps<
      Value,
      Option,
      IsMulti,
      Group,
      Additional
    >,
  ): React.ReactElement;
}

const SelectInputAsyncPaginateCreatable__ = withAsyncPaginate(
  SelectInputCreatable as any,
) as SelectInputAsyncPaginateCreatablePropsType;

type SelectInputAsyncPaginateCreatableBaseProps<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  Additional,
> = SelectInputAsyncPaginateCreatableProps<
  Value,
  Option,
  IsMulti,
  Group,
  Additional
> &
  CopyPasteProps<Value, Option, IsMulti, Group>;

const SelectInputAsyncPaginateCreatable_ = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  Additional,
>(
  props: SelectInputAsyncPaginateCreatableBaseProps<
    Value,
    Option,
    IsMulti,
    Group,
    Additional
  > & { ref?: React.Ref<SelectInstance<Option, IsMulti, Group>> },
  ref: React.Ref<SelectInstance<Option, IsMulti, Group>>,
) => {
  const {
    styles: _styles,
    classNames: _classNames,
    classNamesOptions,
    components,
    menuPortalTarget,
    placeholder = defaultPlaceholder,
    closeMenuOnSelect = !props.isMulti,
    copyPaste: copyPasteEnable,
    ...rest
  } = props;
  const instanceId = React.useId();

  const styles = useStyles({
    styles: _styles,
    options: classNamesOptions,
  });

  const classNames = useClassNames({
    classNames: _classNames,
    options: classNamesOptions,
  });

  const {
    ref: copyPasteRef,
    copy: _copy,
    storeOptions: _storeOptions,
    ...copyPaste
  } = useCopyPaste<Value, Option, IsMulti, Group>(props);
  const _menuPortalTarget = useMenuPortal();

  return (
    <SelectInputAsyncPaginateCreatable__
      {...copyPaste}
      selectRef={combineRefs(ref, copyPasteRef)}
      instanceId={instanceId}
      unstyled
      components={{
        Control,
        DropdownIndicator,
        ClearIndicator,
        MultiValue,
        MultiValueRemove,
        Option,
        Menu,
        // MenuList,
        ...copyPaste.components,
        ...components,
      }}
      styles={styles}
      classNames={classNames}
      menuPortalTarget={firstNotUndefined(
        menuPortalTarget,
        _menuPortalTarget,
        document.body,
      )}
      placeholder={placeholder}
      closeMenuOnSelect={closeMenuOnSelect}
      {...rest}
    />
  );
};

const SelectInputAsyncPaginateCreatable = React.forwardRef(
  SelectInputAsyncPaginateCreatable_,
) as typeof SelectInputAsyncPaginateCreatable_;

export type SelectInputAsyncPaginateMutationCreatableProps<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TData,
  TError,
  TVariables,
  TContext,
> = Omit<
  SelectInputAsyncPaginateCreatableProps<
    Value,
    Option,
    IsMulti,
    Group,
    { page: number }
  >,
  "loadOptions"
> & {
  mutation: UseMutationResult<TData, TError, TVariables, TContext>;
  requestVariable: (
    search: string | undefined,
    page: number,
    limit?: number,
    additional?: { exact?: boolean },
  ) => TVariables;
  createOptions: (data: Awaited<TData>) => Option[];
  hasMore: (data: Awaited<TData>, variable: TVariables) => boolean;
};

export const SelectInputAsyncPaginateMutationCreatableBase = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TData,
  TError,
  TVariables,
  TContext,
>(
  props: SelectInputAsyncPaginateMutationCreatableProps<
    Value,
    Option,
    IsMulti,
    Group,
    TData,
    TError,
    TVariables,
    TContext
  >,
  ref: React.Ref<SelectInstance<Option, IsMulti, Group>>,
) => {
  return (
    <SelectInputAsyncPaginateCreatable
      ref={ref as any}
      {...props}
      loadOptions={useOptionsMutationLoader<
        Value,
        Option,
        IsMulti,
        Group,
        TData,
        TError,
        TVariables,
        TContext
      >(props)}
    />
  );
};

export const SelectInputAsyncPaginateMutationCreatable = React.forwardRef(
  SelectInputAsyncPaginateMutationCreatableBase,
) as <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TData = any,
  TError = ApiError,
  TVariables = void,
  TContext = unknown,
>(
  p: SelectInputAsyncPaginateMutationCreatableProps<
    Value,
    Option,
    IsMulti,
    Group,
    TData,
    TError,
    TVariables,
    TContext
  >,
) => React.ReactElement;

export type SelectInputAsyncPaginateQueryCreatableProps<
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
> = Omit<
  SelectInputAsyncPaginateCreatableProps<
    Value,
    Option,
    IsMulti,
    Group,
    { page: number }
  >,
  "loadOptions"
> & {
  queryCreator: (
    argument: TVariables,
  ) => UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>;
  requestVariable: (
    search: string | undefined,
    page: number,
    limit?: number,
    additional?: { exact?: boolean },
  ) => TVariables | null;
  createOptions: (data: Awaited<TData>) => Option[];
  hasMore: (data: Awaited<TData>, variable: TVariables) => boolean;
};

export const SelectInputAsyncPaginateQueryCreatableBase = <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
>(
  props: SelectInputAsyncPaginateQueryCreatableProps<
    Value,
    Option,
    IsMulti,
    Group,
    TVariables,
    TQueryFnData,
    TError,
    TData,
    TQueryKey
  >,
  ref: React.Ref<SelectInstance<Option, IsMulti, Group>>,
) => {
  const loadOptions = useOptionsQueryLoader<
    Value,
    Option,
    IsMulti,
    Group,
    TVariables,
    TQueryFnData,
    TError,
    TData,
    TQueryKey
  >(props);
  return (
    <SelectInputAsyncPaginateCreatable
      ref={ref as any}
      {...props}
      loadOptions={loadOptions}
    />
  );
};

export const SelectInputAsyncPaginateQueryCreatable = React.forwardRef(
  SelectInputAsyncPaginateQueryCreatableBase,
) as <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
>(
  p: SelectInputAsyncPaginateQueryCreatableProps<
    Value,
    Option,
    IsMulti,
    Group,
    TVariables,
    TQueryFnData,
    TError,
    TData,
    TQueryKey
  > & {
    ref?: React.Ref<React.LegacyRef<SelectInstance<Option, IsMulti, Group>>>;
  },
) => React.ReactElement;

export const SelectInputAsyncPaginateQueryCreatableToggle = React.forwardRef(
  ({ creatable, ...props }, ref) =>
    creatable ? (
      <SelectInputAsyncPaginateQueryCreatable ref={ref} {...props} />
    ) : (
      <SelectInputAsyncPaginateQuery ref={ref} {...props} />
    ),
) as <
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
  TVariables,
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
>(
  p: SelectInputAsyncPaginateQueryCreatableProps<
    Value,
    Option,
    IsMulti,
    Group,
    TVariables,
    TQueryFnData,
    TError,
    TData,
    TQueryKey
  > & {
    creatable?: boolean;
    ref?: React.Ref<React.LegacyRef<SelectInstance<Option, IsMulti, Group>>>;
  },
) => React.ReactElement;
